import { makeAutoObservable, runInAction } from 'mobx'
import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'
import { formatAppDate } from '@/Ship/Helpers/appTime'
import { MAX_ODOMETER } from '@/Ship/Helpers/odometer'

import { type ClosingSummary, closingSummary } from './shiftClosing'

interface ShiftRef {
    uuid?: string
    title?: string
}

export interface DriverShift {
    uuid: string
    code: string
    shiftDate: string
    status: 'new' | 'started' | 'completed'
    vehicle?: ShiftRef | null
    cluster?: ShiftRef | null
    region?: ShiftRef | null
    startedAt?: string | null
    startOdometer?: number | null
}

/** ТЗ, який водій може обрати при відкритті зміни (GET /services/shifts/{uuid}/start-options) */
export interface StartVehicle {
    uuid: string
    title: string | null
    /** Кінцевий одометр останньої завершеної зміни цього ТЗ — мінімум для початкового */
    lastEndOdometer: number | null
}

/** Регіон, у якому водій може створити зміну (GET /services/shifts/open/options); кластер — з регіону */
export interface ShiftRegion {
    code: string
    title: string | null
    cluster: { code: string | null; title: string | null }
}

/**
 * Чи може водій створити зміну сам (рішення 2026-10-30): відкрити нічого — немає розпочатої
 * й немає нової на сьогодні чи раніше (майбутні нові не заважають). Ті самі правила — на бекенді.
 */
export const canCreateShift = (shifts: DriverShift[], today: string): boolean =>
    !shifts.some((s) => s.status === 'started' || (s.status === 'new' && shiftDay(s.shiftDate) <= today))

/** Завдання з точки зору зміни: ТЗ і кластер */
export interface ShiftScopedTask {
    vehicle?: { uuid?: string | null } | null
    cluster?: { uuid?: string | null } | null
}

/** Пункти чек-листа готовності — дослівно з вимог; кожен підтверджується окремо */
export const CHECKLIST = {
    documents: 'Підтверджую, що маю всі необхідні документи для здійснення перевезень',
    safetyBriefing: 'Підтверджую, що пройшов необхідний інструктаж з безпеки руху',
} as const

const DATE_ONLY = /^(\d{4}-\d{2}-\d{2})/

/** YYYY-MM-DD з дати зміни (бекенд віддає ISO з часом 00:00 і поясом) */
export const shiftDay = (value: string | null | undefined): string => DATE_ONLY.exec(value ?? '')?.[1] ?? ''

/** 2026-10-02 → 02.10.2026 */
export const formatShiftDay = (value: string | null | undefined): string => {
    const [y, m, d] = shiftDay(value).split('-')
    return y && m && d ? `${d}.${m}.${y}` : '—'
}

/** Незакриті зміни (усі, крім завершених) за датою, найраніші — першими */
export const sortShifts = (shifts: DriverShift[]): DriverShift[] =>
    shifts
        .filter((s) => s.status !== 'completed')
        .sort((a, b) => shiftDay(a.shiftDate).localeCompare(shiftDay(b.shiftDate)) || a.code.localeCompare(b.code))

/**
 * Чому зміну зараз не можна відкрити (null — можна). Ті самі правила перевіряє бекенд:
 * одна розпочата зміна; майбутню не відкрити; спершу — незакриті зміни за попередні дати.
 */
export const startBlockReason = (shift: DriverShift, shifts: DriverShift[], today: string): string | null => {
    if (shift.status !== 'new') return null
    if (shifts.some((s) => s.status === 'started')) return 'Спершу завершіть поточну зміну'

    const day = shiftDay(shift.shiftDate)
    if (day > today) return 'Можна відкрити в день зміни'

    const earlier = sortShifts(shifts).find((s) => s.uuid !== shift.uuid && shiftDay(s.shiftDate) < day)
    return earlier ? `Спершу завершіть зміну за ${formatShiftDay(earlier.shiftDate)}` : null
}

/** Завдання в межах зміни: той самий ТЗ і кластер (як фільтр списку на бекенді) */
export const shiftCovers = (shift: DriverShift | null, task: ShiftScopedTask): boolean => {
    const vehicle = task.vehicle?.uuid
    const cluster = task.cluster?.uuid
    return !!shift && !!vehicle && !!cluster && vehicle === shift.vehicle?.uuid && cluster === shift.cluster?.uuid
}

/** Крок закриття зміни: попередження → дані (одометр, зауваження, фото) → перевірка */
export type CloseStep = 'warn' | 'form' | 'review'

/** Фото несправностей не більше — як ліміт бекенду (UploadShiftPhotoAction::MAX_PHOTOS) */
export const MAX_SHIFT_PHOTOS = 10

/** Фото несправності у формі закриття; uploadedUuid — уже завантажена чернетка на сервері */
export interface ClosePhoto {
    id: string
    file: File
    preview: string
    uploadedUuid?: string | undefined
}

/** Прев'ю фото — object URL (не base64 у пам'яті); звільняємо, коли фото прибрали */
const releasePreview = (preview: string): void => {
    if (preview.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(preview)
}

/** Текст блокування закриття — той самий, що віддає сервер (validator.shift.endOdometer) */
export const END_ODOMETER_BELOW_START =
    'Кінцевий показник одометра не може бути меншим за показник на початку зміни. Перевірте введені дані.'

/** Ціле невід'ємне число з поля вводу не більше MAX_ODOMETER; null — некоректне, задовге або порожнє */
export const parseOdometer = (raw: string): number | null => {
    const value = raw.trim()
    if (!/^\d{1,10}$/.test(value)) return null
    const number = Number(value)
    return number <= MAX_ODOMETER ? number : null
}

/** Чому показник одометра не розібрався: не число або більше за межу */
const odometerFormatError = (raw: string): string =>
    /^\d+$/.test(raw.trim())
        ? `Не більше ${MAX_ODOMETER.toLocaleString('uk-UA').replace(/[\u00a0\u202f]/g, ' ')} км`
        : 'Введіть ціле число кілометрів'

/** Текст помилки з відповіді бекенду (422 — перше порушення, інакше message) */
const errorText = (err: any, fallback: string): string => {
    const violations = err?.response?.violations
    if (Array.isArray(violations) && violations[0]?.message) return violations[0].message
    return err?.response?.message || fallback
}

/**
 * Обмеження зміни діють лише на водія: адмін (успадковує роль водія) і диспетчер працюють
 * без них — як DriverShiftPolicy на бекенді
 */
const STAFF_ROLES = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SENIOR_DISPATCHER', 'ROLE_DISPATCHER']
const isRestrictedDriver = (): boolean => !userManager.hasAnyRole(STAFF_ROLES)

/** Сьогодні в поясі застосунку (Київ), а не пристрою — як і дати змін на бекенді */
const localToday = (): string => formatAppDate(new Date().toISOString(), 'YYYY-MM-DD') ?? ''

/** Останній отриманий список змін: офлайн PWA має знати активну зміну (кнопки завдань) */
const cacheKey = (): string => `autostrada:driver-shifts:${userManager.user?.['uuid'] ?? 'anonymous'}`

const readCache = (): DriverShift[] | null => {
    try {
        const raw = window.localStorage.getItem(cacheKey())
        const items = raw ? JSON.parse(raw) : null
        return Array.isArray(items) ? items : null
    } catch {
        return null
    }
}

const writeCache = (items: DriverShift[]): void => {
    try {
        window.localStorage.setItem(cacheKey(), JSON.stringify(items))
    } catch {
        // Сховище недоступне (приватний режим) — офлайн просто не буде кешу
    }
}

/**
 * Блок «Моя зміна» в кабінеті водія. Робота починається з відкриття зміни: водій обирає
 * одну з незакритих змін (або, коли відкрити нічого, створює нову на сьогодні з регіоном —
 * вона одразу розпочата), підтверджує або змінює ТЗ, вносить початковий одометр
 * (підставляється кінцевий з попередньої зміни ТЗ, менше — не можна) і окремо
 * підтверджує пункти чек-листа. Завдання — лише в межах активної (розпочатої) зміни.
 * Закриття — у три кроки (closeStep): попередження, дані закриття з фото несправностей,
 * перевірка підсумку. Відкриття й закриття — лише онлайн.
 */
export class MyShiftStore {
    /** Незакриті зміни водія (нові й розпочата) */
    shifts: DriverShift[] = []
    loading = false
    loadError: string | null = null
    /** Список змін узято з кешу (офлайн) */
    fromCache = false
    /** Хоча б раз отримали стан змін (з мережі або кешу) */
    loaded = false

    /** Зміна, яку водій відкриває (поп-ап «Відкриття зміни») */
    startShift: DriverShift | null = null
    /** Поп-ап у режимі «Створити зміну»: нова зміна на сьогодні, одразу розпочата */
    creating = false
    /**
     * Номер сеансу поп-апу відкриття/створення: відповідь на запит опцій, що прийшла після
     * закриття чи відкриття іншого поп-апу, не має потрапити в новий
     */
    startSession = 0
    /** Дата нової зміни (сьогодні за сервером) */
    createDate: string | null = null
    regions: ShiftRegion[] = []
    regionCode: string | null = null
    /** Крок закриття зміни; null — шторка закрита */
    closeStep: CloseStep | null = null
    submitting = false
    formError: string | null = null

    vehicles: StartVehicle[] = []
    vehiclesLoading = false
    vehicleUuid: string | null = null
    startOdometer = ''
    documentsConfirmed = false
    safetyBriefingConfirmed = false
    endOdometer = ''
    technicalRemarks = ''
    photos: ClosePhoto[] = []
    /** Фото стискаються перед додаванням у форму */
    photosProcessing = false
    /** «Зараз» на кроці перевірки: час завершення й тривалість у підсумку */
    reviewAt: string | null = null
    /**
     * Номер сеансу закриття: фото, що ще стискалися, коли шторку закрили й відкрили знову,
     * не мають потрапити в новий сеанс
     */
    closeSession = 0

    /** Сьогоднішня дата YYYY-MM-DD (у тестах — фіксована) */
    private readonly today: () => string

    /** Чи діють на користувача обмеження зміни (у тестах — фіксовано) */
    private readonly restricted: () => boolean

    constructor(today: () => string = localToday, restricted: () => boolean = isRestrictedDriver) {
        this.today = today
        this.restricted = restricted
        makeAutoObservable<this, 'today' | 'restricted'>(this, { today: false, restricted: false })
    }

    /** Активна (розпочата) зміна */
    get active(): DriverShift | null {
        return this.shifts.find((s) => s.status === 'started') ?? null
    }

    /** Зміни, які можна відкрити або які чекають черги (без активної) */
    get pending(): DriverShift[] {
        return sortShifts(this.shifts).filter((s) => s.status === 'new')
    }

    /** Водій без активної зміни: завдань він не бачить, доки не відкриє зміну */
    get waitingForShift(): boolean {
        return this.restricted() && this.loaded && !this.active
    }

    get startOpen(): boolean {
        return this.startShift !== null || this.creating
    }

    /** Показати «Створити зміну»: лише водієві, онлайн-дані й коли відкрити нічого */
    get canCreate(): boolean {
        return this.restricted() && this.loaded && !this.fromCache && canCreateShift(this.shifts, this.today())
    }

    get selectedRegion(): ShiftRegion | null {
        return this.regions.find((r) => r.code === this.regionCode) ?? null
    }

    get completeOpen(): boolean {
        return this.closeStep !== null
    }

    blockReason(shift: DriverShift): string | null {
        return startBlockReason(shift, this.shifts, this.today())
    }

    /**
     * Чому водій не може діяти із завданням (статус, затримка); null — може. Перегляд
     * завдання не обмежується: зі сповіщення відкривається будь-яке.
     */
    taskActionBlockReason(task: ShiftScopedTask): string | null {
        if (!this.restricted() || !this.loaded) return null
        if (!this.active) return 'Відкрийте зміну, щоб працювати із завданнями'
        return shiftCovers(this.active, task)
            ? null
            : 'Завдання не належить до вашої активної зміни (інший ТЗ або кластер)'
    }

    async fetch(): Promise<void> {
        this.loading = true
        this.loadError = null
        try {
            const filters = JSON.stringify({ and: [{ field: 'status', op: 'in', value: ['new', 'started'] }], or: [] })
            // forceRefresh: без нього restClient віддає кеш, і після дії картка не оновиться
            const data: any = await restClient.get(
                '/services/shifts',
                { searchParams: { filters, limit: 100 } },
                { forceRefresh: true }
            )
            const items = (data?.items ?? []) as DriverShift[]
            writeCache(items)
            runInAction(() => {
                this.shifts = items
                this.fromCache = false
                this.loaded = true
            })
        } catch {
            const cached = readCache()
            runInAction(() => {
                if (cached) {
                    this.shifts = cached
                    this.fromCache = true
                    this.loaded = true
                } else {
                    this.loadError = 'Не вдалося завантажити зміни'
                }
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    /** Мінімальний початковий одометр для обраного ТЗ */
    get minOdometer(): number | null {
        return this.vehicles.find((v) => v.uuid === this.vehicleUuid)?.lastEndOdometer ?? null
    }

    get startOdometerError(): string | null {
        if (this.startOdometer.trim() === '') return null
        const value = parseOdometer(this.startOdometer)
        if (value === null) return odometerFormatError(this.startOdometer)
        const min = this.minOdometer
        if (min !== null && value < min) return `Не менше за кінцевий одометр попередньої зміни (${min} км)`
        return null
    }

    get startValid(): boolean {
        return (
            (!this.creating || this.regionCode !== null) &&
            this.vehicleUuid !== null &&
            parseOdometer(this.startOdometer) !== null &&
            this.startOdometerError === null &&
            this.documentsConfirmed &&
            this.safetyBriefingConfirmed
        )
    }

    get endOdometerError(): string | null {
        if (this.endOdometer.trim() === '') return null
        const end = parseOdometer(this.endOdometer)
        if (end === null) return odometerFormatError(this.endOdometer)
        const start = this.active?.startOdometer
        if (typeof start === 'number' && end < start) return END_ODOMETER_BELOW_START
        return null
    }

    get completeValid(): boolean {
        return parseOdometer(this.endOdometer) !== null && this.endOdometerError === null
    }

    /** Підсумок для кроку перевірки; null — поза ним */
    get summary(): ClosingSummary | null {
        const shift = this.active
        if (!shift || this.closeStep !== 'review' || !this.reviewAt) return null
        return closingSummary(shift, parseOdometer(this.endOdometer), this.reviewAt)
    }

    /** Порожня форма відкриття (спільна для «Відкрити зміну» і «Створити зміну») */
    private resetStartForm(): void {
        this.vehicles = []
        this.vehicleUuid = null
        this.startOdometer = ''
        this.documentsConfirmed = false
        this.safetyBriefingConfirmed = false
        this.regions = []
        this.regionCode = null
        this.createDate = null
        this.formError = null
    }

    /** Відкрити поп-ап і завантажити доступні ТЗ (запланований ТЗ зміни — обраний за замовчуванням) */
    async openStart(shift: DriverShift): Promise<void> {
        this.creating = false
        this.startShift = shift
        this.resetStartForm()
        const session = ++this.startSession
        this.vehiclesLoading = true
        try {
            const data: any = await restClient.get(
                `/services/shifts/${shift.uuid}/start-options`,
                {},
                { forceRefresh: true }
            )
            runInAction(() => {
                if (session !== this.startSession) return
                this.vehicles = (data?.vehicles ?? []) as StartVehicle[]
                const current = data?.currentVehicle as string | null
                if (current && this.vehicles.some((v) => v.uuid === current)) this.setVehicle(current)
            })
        } catch (err: any) {
            runInAction(() => {
                if (session === this.startSession) {
                    this.formError = errorText(err, 'Не вдалося завантажити транспортні засоби')
                }
            })
        } finally {
            runInAction(() => {
                if (session === this.startSession) this.vehiclesLoading = false
            })
        }
    }

    /** «Створити зміну»: поп-ап відкриття з датою (сьогодні) і регіоном; ТЗ і регіони — з сервера */
    async openCreate(): Promise<void> {
        this.startShift = null
        this.creating = true
        this.resetStartForm()
        const session = ++this.startSession
        this.vehiclesLoading = true
        try {
            const data: any = await restClient.get('/services/shifts/open/options', {}, { forceRefresh: true })
            runInAction(() => {
                if (session !== this.startSession) return
                this.vehicles = (data?.vehicles ?? []) as StartVehicle[]
                this.regions = (data?.regions ?? []) as ShiftRegion[]
                this.createDate = typeof data?.date === 'string' ? data.date : this.today()
            })
        } catch (err: any) {
            runInAction(() => {
                if (session === this.startSession) {
                    this.formError = errorText(err, 'Не вдалося завантажити дані для нової зміни')
                }
            })
        } finally {
            runInAction(() => {
                if (session === this.startSession) this.vehiclesLoading = false
            })
        }
    }

    setRegion(code: string | null): void {
        this.regionCode = code
    }

    /** «Закрити зміну»: спершу попередження, дані — з чистого аркуша */
    openComplete(): void {
        this.endOdometer = ''
        this.technicalRemarks = ''
        this.photos.forEach((photo) => releasePreview(photo.preview))
        this.photos = []
        this.closeSession++
        this.reviewAt = null
        this.formError = null
        this.closeStep = 'warn'
    }

    /** «Продовжити закриття» → форма даних закриття */
    continueClosing(): void {
        this.formError = null
        this.closeStep = 'form'
    }

    /** До перевірки — лише з коректним кінцевим одометром */
    toReview(): void {
        if (!this.completeValid) return
        this.formError = null
        this.reviewAt = new Date().toISOString()
        this.closeStep = 'review'
    }

    /** «Змінити дані» з кроку перевірки */
    backToForm(): void {
        if (this.submitting) return
        this.formError = null
        this.closeStep = 'form'
    }

    /** Оновити «зараз» у підсумку, поки водій його читає */
    tickReview(): void {
        if (this.closeStep === 'review') this.reviewAt = new Date().toISOString()
    }

    close(): void {
        if (this.submitting) return
        this.startShift = null
        this.creating = false
        this.startSession++
        this.closeStep = null
        this.closeSession++
    }

    get canAddPhoto(): boolean {
        return this.photos.length < MAX_SHIFT_PHOTOS && !this.photosProcessing
    }

    setPhotosProcessing(value: boolean): void {
        this.photosProcessing = value
    }

    /** false — ліміт фото вже вичерпано або фото з попереднього сеансу закриття */
    addPhoto(file: File, preview: string, session: number = this.closeSession): boolean {
        if (session !== this.closeSession || this.closeStep === null || this.photos.length >= MAX_SHIFT_PHOTOS) {
            releasePreview(preview)
            return false
        }
        this.photos = [...this.photos, { id: `${Date.now()}-${this.photos.length}-${file.name}`, file, preview }]
        return true
    }

    removePhoto(id: string): void {
        this.photos.filter((photo) => photo.id === id).forEach((photo) => releasePreview(photo.preview))
        this.photos = this.photos.filter((photo) => photo.id !== id)
    }

    setFormError(value: string | null): void {
        this.formError = value
    }

    /** Обрати ТЗ: початковий одометр — кінцевий з попередньої зміни цього ТЗ (якщо є) */
    setVehicle(uuid: string | null): void {
        this.vehicleUuid = uuid
        const min = this.minOdometer
        this.startOdometer = min === null ? '' : String(min)
    }

    setStartOdometer(value: string): void {
        this.startOdometer = value
    }

    setDocumentsConfirmed(value: boolean): void {
        this.documentsConfirmed = value
    }

    setSafetyBriefingConfirmed(value: boolean): void {
        this.safetyBriefingConfirmed = value
    }

    setEndOdometer(value: string): void {
        this.endOdometer = value
    }

    setTechnicalRemarks(value: string): void {
        this.technicalRemarks = value
    }

    /** true — зміну відкрито (або створено й відкрито) */
    async start(isOffline = false): Promise<boolean> {
        if (this.creating) {
            if (!this.startValid) return false
            return this.submit(
                isOffline,
                '/services/shifts/open',
                {
                    // Дата, яку бачив водій: опівночі сервер відмовить, а не створить на іншу
                    date: this.createDate,
                    vehicle: this.vehicleUuid,
                    region: this.regionCode,
                    startOdometer: parseOdometer(this.startOdometer),
                    documentsConfirmed: true,
                    safetyBriefingConfirmed: true,
                },
                'Не вдалося створити зміну'
            )
        }
        const shift = this.startShift
        if (!shift || !this.startValid) return false
        return this.submit(
            isOffline,
            `/services/shifts/${shift.uuid}/start`,
            {
                vehicle: this.vehicleUuid,
                startOdometer: parseOdometer(this.startOdometer),
                documentsConfirmed: true,
                safetyBriefingConfirmed: true,
            },
            'Не вдалося відкрити зміну'
        )
    }

    /**
     * true — зміну закрито. Фото вантажаться по одному (ліміт запиту — 10 МБ) чернетками
     * зміни, потім закриття прикріплює їх. confirmedAt — момент «Закрити зміну» на пристрої.
     */
    async complete(isOffline = false, confirmedAt: string = new Date().toISOString()): Promise<boolean> {
        const shift = this.active
        if (!shift || !this.completeValid || this.submitting) return false
        if (isOffline) {
            this.formError = "Немає з'єднання. Спробуйте, коли з'явиться мережа."
            return false
        }

        this.submitting = true
        this.formError = null
        let photos: string[]
        try {
            photos = await this.uploadPhotos(shift.uuid)
        } catch (err: any) {
            runInAction(() => {
                this.formError = errorText(err, 'Не вдалося завантажити фото. Спробуйте ще раз.')
                this.submitting = false
            })
            return false
        }

        const done = await this.submit(
            false,
            `/services/shifts/${shift.uuid}/complete`,
            {
                endOdometer: parseOdometer(this.endOdometer),
                technicalRemarks: this.technicalRemarks.trim() || null,
                photos,
                confirmedAt,
            },
            'Не вдалося закрити зміну'
        )
        if (done) return true

        // Відповідь могла загубитися, хоча сервер зміну вже закрив: перечитуємо стан, і якщо
        // зміна більше не розпочата — закриття вдалося, повторювати нічого
        const error = this.formError
        await this.fetch()
        if (!this.fromCache && !this.shifts.some((s) => s.uuid === shift.uuid && s.status === 'started')) {
            runInAction(() => {
                this.closeStep = null
                this.formError = null
            })
            return true
        }
        runInAction(() => {
            this.formError = error
            // Чернетку могло витіснити новішими — при повторі завантажуємо всі фото заново
            this.photos = this.photos.map((photo) => ({ ...photo, uploadedUuid: undefined }))
        })
        return false
    }

    /** Завантажити ще не завантажені фото; повертає uuid усіх фото форми */
    private async uploadPhotos(shiftUuid: string): Promise<string[]> {
        const uuids: string[] = []
        for (const photo of this.photos) {
            if (!photo.uploadedUuid) {
                const body = new FormData()
                body.append('file', photo.file, photo.file.name)
                // isNotHeaderAddContentType: інакше клієнт поставить application/ld+json і зламає multipart
                const result = (await restClient.post(
                    `/services/shifts/${shiftUuid}/photos`,
                    { body },
                    { isNotHeaderAddContentType: true }
                )) as unknown as { uuid?: string } | null
                if (!result?.uuid) throw new Error('upload without uuid')
                const uuid = result.uuid
                runInAction(() => {
                    photo.uploadedUuid = uuid
                })
            }
            uuids.push(photo.uploadedUuid as string)
        }
        return uuids
    }

    private async submit(
        isOffline: boolean,
        url: string,
        json: Record<string, unknown>,
        fallback: string
    ): Promise<boolean> {
        if (isOffline) {
            this.formError = "Немає з'єднання. Спробуйте, коли з'явиться мережа."
            return false
        }

        this.submitting = true
        this.formError = null
        try {
            await restClient.post(url, { json })
            runInAction(() => {
                this.startShift = null
                this.creating = false
                this.closeStep = null
            })
            await this.fetch()
            return true
        } catch (err: any) {
            runInAction(() => {
                this.formError = errorText(err, fallback)
            })
            return false
        } finally {
            runInAction(() => {
                this.submitting = false
            })
        }
    }
}

/** Один стан змін на кабінет водія: його читають блок «Моя зміна», список і картка завдання */
export const myShiftStore = new MyShiftStore()
